import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientServices } from '../../services/clientServices';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../services/authentication/authContext';
import { Button, IconButton, Tooltip, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PageHeader from '../ui/PageHeader';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';
import ConfirmationDialog from '../common/ConfirmationDialog';
import TableSkeleton from '../common/TableSkeleton';
import EmptyState from '../common/EmptyState';
import ErrorState from '../common/ErrorState';
import { useSnackbar } from '../../utils/useSnackbar';

const Clients = () => {
  const [clientData, setClientData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const navigationUrl = "/home/client";
  const [searchClient, setSearchClient] = useState('');
  const [debouncedSearchClient, setDebouncedSearchClient] = useState(searchClient);
  const [filterClient, setFilterClient] = useState('');
  const [debouncedFilterClient, setDebouncedFilterClient] = useState(filterClient);
  const isSearchFirstRender = useRef(true);
  const isFilterFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);
  const [sortModel, setSortModel] = useState([{ field: 'updatedAt', sort: 'desc' }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const pageSizeOptions = [5, 10, 15];
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const sortField = sortModel[0]?.field || "updatedAt";
      const sortDir = sortModel[0]?.sort || "desc";

      const response = await clientServices.getClientData(debouncedSearchClient, debouncedFilterClient, paginationModel.page, paginationModel.pageSize, sortField, sortDir);
      setClientData(response.content);
      setRowCount(response.totalElements);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setClientData([]);
        setRowCount(0);
      } else {
        console.error("Error fetching data", error);
        setError('Failed to load clients. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
  }, [accessToken, paginationModel, debouncedSearchClient, debouncedFilterClient, sortModel]);

  useEffect(() => {
    const message = localStorage.getItem("successMessage");
    if (message) {
      showSnackbar(message, 'success');
      localStorage.removeItem("successMessage");
    }
  }, []);

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const handleAddClient = () => {
    if (accessToken) {
      navigate(navigationUrl);
    }
  };

  const handleEditClient = (client) => {
    if (accessToken) {
      navigate(navigationUrl, { state: { clientData: client } });
    }
  };

  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchClient.trim();

      if (value === "" || value.length > 2) {
        setDebouncedSearchClient(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchClient]);

  useEffect(() => {
    if (isFilterFirstRender.current) {
      isFilterFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = filterClient.trim();

      if (value === "" || value.length > 2) {
        setDebouncedFilterClient(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [filterClient]);

  const columns = useMemo(() => [
    {
      field: 'name', headerName: "Client", flex: 2, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'code', headerName: t('client.code'), flex: 2, minWidth: isMobile ? 100 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'contactPersonName', headerName: "Contact Person", flex: 3, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'emailId', headerName: t('client.email'), flex: 3, minWidth: isMobile ? 200 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'mobileNo', headerName: t('client.mobile_no'), flex: 2, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'address', headerName: t('client.address'), flex: 3, minWidth: isMobile ? 200 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value}</span></Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: t('client.actions'),
      flex: 2,
      minWidth: isMobile ? 100 : 0,
      renderCell: (params) => (
        <div>
          <IconButton
            color="primary"
            onClick={() => handleEditClient(params.row)}
            className="icon-button"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            color="secondary"
            onClick={() => handleDeleteClient(params.row.id)}
            className="icon-button"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ], [isMobile, t]);

  const filteredDataMemo = useMemo(() => {
    if (!clientData) return [];

    const filter = (client, search) => {
      const lowerSearch = search?.trim().toLowerCase() || '';
      return (
        client?.name?.toLowerCase().includes(lowerSearch) ||
        client?.code?.toLowerCase().includes(lowerSearch) ||
        client?.contactPersonName?.toLowerCase().includes(lowerSearch) ||
        client?.emailId?.toLowerCase().includes(lowerSearch) ||
        client?.mobileNo?.toLowerCase().includes(lowerSearch) ||
        client?.address?.toLowerCase().includes(lowerSearch)
      );
    };

    return clientData
      .filter(client => filter(client, searchClient) && filter(client, filterClient));
  }, [clientData, searchClient, filterClient]);

  const clientColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        // code: false,
        // contactPersonName: false,
        // address: false,
      };
    }

    if (isTabletOrBelow) {
      return {
        // address: false,
        // mobileNo: false,
      };
    }

    return {};
  }, [isMobile, isTabletOrBelow]);

  const handleDeleteClient = (clientId) => {
    setClientToDelete(clientId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setClientToDelete(null);
  };

  const confirmDelete = () => {
    if (clientToDelete) {
      clientServices.deleteClientData(clientToDelete)
        .then(() => {
          setClientData(prevData => prevData.filter(client => client.id !== clientToDelete));
          showSnackbar('Client deleted successfully!', 'success');
        })
        .catch(() => {
          showSnackbar('Error deleting client data', 'error');
        });
    }
    handleCloseDialog();
  };

  return (
    <div className="data-table-container">
      <PageHeader title={t('client.clients')} />

      {/* Snackbar */}
      {SnackbarComponent}

      <div className="search-and-button">
        <Button
          variant="contained"
          onClick={handleAddClient}
          className="add-button"
          startIcon={<AddIcon />}
        >
          {t('job.add')}
        </Button>

        {/* General Search Filter */}
        <div className="search-container">
          <input
            type="text"
            placeholder="Type to search..."
            value={searchClient}
            onChange={(e) => setSearchClient(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="data-table">
        <div className="data-grid-container" style={{ height: '70vh' }}>
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={6} />
            </Box>
          ) : error ? (
            <Box sx={{ height: '70vh', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ErrorState
                title="Could not load clients"
                description="We couldn’t fetch clients from the server."
                details={typeof error === 'string' ? error : undefined}
                action={
                  <Button
                    variant="contained"
                    onClick={fetchData}
                    sx={{ mt: 1, backgroundColor: 'var(--primary)' }}
                  >
                    Retry
                  </Button>
                }
              />
            </Box>
          ) : filteredDataMemo?.length === 0 ? (
            <Box sx={{ height: '70vh', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyState title="No clients found" description="Try clearing filters or adjusting your search." />
            </Box>
          ) : (
            <DataGrid
              loading={false}
              rows={filteredDataMemo}
              columns={columns}
              pagination
              paginationMode="server"
              filterMode="server"
              onFilterModelChange={(newFilterModel) => {
                const value = newFilterModel.items?.[0]?.value || '';
                setFilterClient((prev) => (prev === value ? prev : value));
              }}
              sortingMode="server"
              onSortModelChange={(newModel) => {
                if (isSortFirstRender.current) {
                  isSortFirstRender.current = false;
                  if (!newModel?.length) return;
                }
                const next = newModel?.[0];
                const current = sortModel?.[0];
                if (next?.field === current?.field && next?.sort === current?.sort) return;
                if (!newModel?.length && sortModel.length > 0) {
                  const currentField = sortModel[0].field;
                  const isColumnDefined = columns.some(c => c.field === currentField);

                  if (!isColumnDefined) {
                    return;
                  }
                }
                setSortModel(newModel);
                setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
              }}
              sortModel={sortModel}
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={handlePaginationModelChange}
              columnVisibilityModel={clientColumnVisibilityModel}
            />
          )}
        </div>

        <ConfirmationDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={confirmDelete}
          title="Delete Confirmation"
          message="Are you sure you want to delete the client?"
          messageSecond="*Associated Jobs and Tasks with this client are also be deleted."
          cancelLabel="Cancel"
          confirmLabel="Delete"
          isDelete={true}
        />
      </div>
    </div>
  );
};

export default Clients;